import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Save, Printer, AlertCircle, FileText, User, Calendar, Stethoscope, Pill, Activity } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api';
import SignatureCanvas from 'react-signature-canvas';

const CMS485TemplateForm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const patientId = searchParams.get('patientId');
  const physicianSignatureRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    // Item 1: Patient Name and Address
    patientName: '',
    patientAddress: '',
    patientCity: '',
    patientState: '',
    patientZip: '',
    
    // Item 2: Start of Care Date
    startOfCareDate: '',
    
    // Item 3: Certification Period
    certificationFromDate: '',
    certificationToDate: '',
    
    // Item 4: Medical Record Number
    medicalRecordNumber: '',
    
    // Item 5: Provider Number
    providerNumber: '',
    
    // Item 6: Patient Date of Birth
    dateOfBirth: '',
    
    // Item 7: Sex
    sex: '',
    
    // Item 8: Physician Name and Address
    physicianName: '',
    physicianAddress: '',
    physicianCity: '',
    physicianState: '',
    physicianZip: '',
    physicianPhone: '',
    
    // Item 9: Date of Verbal Order
    verbalOrderDate: '',
    
    // Item 10: Primary Diagnosis and ICD Code
    primaryDiagnosis: '',
    primaryDiagnosisCode: '',
    primaryDiagnosisDate: '',
    
    // Item 11: Surgical Procedures
    surgicalProcedure1: '',
    surgicalProcedure1Code: '',
    surgicalProcedure1Date: '',
    surgicalProcedure2: '',
    surgicalProcedure2Code: '',
    surgicalProcedure2Date: '',
    
    // Item 12: Other Diagnoses
    otherDiagnosis1: '',
    otherDiagnosis1Code: '',
    otherDiagnosis2: '',
    otherDiagnosis2Code: '',
    otherDiagnosis3: '',
    otherDiagnosis3Code: '',
    otherDiagnosis4: '',
    otherDiagnosis4Code: '',
    otherDiagnosis5: '',
    otherDiagnosis5Code: '',
    
    // Item 13: DME and Supplies
    dmeAndSupplies: '',
    
    // Item 14: Safety Measures
    safetyMeasures: '',
    
    // Item 15: Allergies
    allergies: '',
    
    // Item 16: Nutritional Requirements
    nutritionalRequirements: '',
    
    // Item 17: Functional Limitations
    functionalLimitations: {
      amputation: false,
      bowelBladder: false,
      contracture: false,
      hearing: false,
      paralysis: false,
      endurance: false,
      ambulation: false,
      speech: false,
      legallyBlind: false,
      dyspnea: false,
      other: false,
      otherDescription: '',
    },
    
    // Item 18: Activities Permitted
    activitiesPermitted: {
      bedrest: false,
      bedrestBRP: false,
      upAsToleraded: false,
      transferBedChair: false,
      exercises: false,
      partialWeightBearing: false,
      independentAtHome: false,
      crutches: false,
      cane: false,
      wheelchair: false,
      walker: false,
      noRestrictions: false,
      other: false,
      otherDescription: '',
    },
    
    // Item 19: Mental Status
    mentalStatus: {
      oriented: false,
      comatose: false,
      forgetful: false,
      depressed: false,
      disoriented: false,
      lethargic: false,
      agitated: false,
      other: false,
      otherDescription: '',
    },
    
    // Item 20: Prognosis
    prognosis: '',
    
    // Item 21: Therapy Services
    therapyOrders: '',
    
    // Item 22: Goals/Rehabilitation Potential
    goalsRehabPotential: '',
    
    // Item 23: Discharge Plans
    dischargePlans: '',
    
    // Item 24: Medications
    medications: [
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
      { name: '', dosage: '', frequency: '', route: '' },
    ],
    
    // Item 25: Physician Signature
    physicianSignatureDate: '',
    
    // Certification
    attendingPhysicianCertification: false,
  });

  useEffect(() => {
    if (patientId) {
      fetchPatientData();
      fetchExistingForm();
    }
  }, [patientId]);

  const fetchExistingForm = async () => {
    try {
      const response = await api.get(`/admission-forms/${patientId}/cms_485`);
      if (response.data && response.data.form && response.data.form.data) {
        const savedData = response.data.form.data;
        setFormData(prev => ({
          ...prev,
          ...savedData,
          // Handle nested objects
          functionalLimitations: { ...prev.functionalLimitations, ...(savedData.functionalLimitations || {}) },
          activitiesPermitted: { ...prev.activitiesPermitted, ...(savedData.activitiesPermitted || {}) },
          mentalStatus: { ...prev.mentalStatus, ...(savedData.mentalStatus || {}) },
          medications: savedData.medications || prev.medications,
        }));
        // Load signature if exists
        if (savedData.physicianSignature && physicianSignatureRef.current) {
          physicianSignatureRef.current.fromDataURL(savedData.physicianSignature);
        }
      }
    } catch (error) {
      console.log('No existing CMS-485 form found, will create new');
    }
  };

  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/patients/${patientId}`);
      if (response.data) {
        const patient = response.data.patient || response.data;
        setFormData(prev => ({
          ...prev,
          medicalRecordNumber: patient.medicalRecordNumber || prev.medicalRecordNumber,
          patientName: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
          patientAddress: patient.address || '',
          patientCity: patient.city || '',
          patientState: patient.state || '',
          patientZip: patient.zipCode || '',
          dateOfBirth: patient.dateOfBirth || '',
          sex: patient.gender || '',
          physicianName: patient.physicianName || patient.primaryPhysician || '',
          physicianPhone: patient.physicianPhone || patient.primaryPhysicianPhone || '',
          primaryDiagnosis: patient.primaryDiagnosis || '',
          allergies: patient.allergies || '',
        }));
      }
    } catch (error) {
      console.error('Error fetching patient:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('functionalLimitations_')) {
      const key = name.replace('functionalLimitations_', '');
      setFormData(prev => ({
        ...prev,
        functionalLimitations: { ...prev.functionalLimitations, [key]: type === 'checkbox' ? checked : value }
      }));
    } else if (name.startsWith('activitiesPermitted_')) {
      const key = name.replace('activitiesPermitted_', '');
      setFormData(prev => ({
        ...prev,
        activitiesPermitted: { ...prev.activitiesPermitted, [key]: type === 'checkbox' ? checked : value }
      }));
    } else if (name.startsWith('mentalStatus_')) {
      const key = name.replace('mentalStatus_', '');
      setFormData(prev => ({
        ...prev,
        mentalStatus: { ...prev.mentalStatus, [key]: type === 'checkbox' ? checked : value }
      }));
    } else {
      setFormData(prev => ({ 
        ...prev, 
        [name]: type === 'checkbox' ? checked : value 
      }));
    }
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData(prev => {
      const newMeds = [...prev.medications];
      newMeds[index] = { ...newMeds[index], [field]: value };
      return { ...prev, medications: newMeds };
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const signatureData = physicianSignatureRef.current && !physicianSignatureRef.current.isEmpty()
        ? physicianSignatureRef.current.toDataURL() : '';

      const payload = {
        ...formData,
        physicianSignature: signatureData,
        patientId,
        formType: 'cms_485',
      };

      const response = await api.post('/admission-forms/save', payload);
      if (response.data.patientUpdated) {
        toast.success('CMS-485 saved & synced!', {
          description: 'Data synced to Patient Demographics, AI Document Generator, and Nursing docs.'
        });
      } else {
        toast.success('CMS-485 Plan of Care saved successfully');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  if (!patientId) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600" />
              <p className="text-yellow-800">Please select a patient to complete this form.</p>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="cms-485-form">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">CMS-485 Home Health Certification and Plan of Care</h1>
                <p className="text-blue-100">Form CMS-485 (C-3) (02-94)</p>
              </div>
            </div>
            <div className="flex gap-2 print:hidden">
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-white text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium"
                data-testid="save-form-btn"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Form'}
              </button>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-b-xl shadow-lg">
          {/* Top Row - Items 1-9 */}
          <div className="grid grid-cols-12 border-b divide-x divide-gray-200">
            {/* Item 1: Patient Info */}
            <div className="col-span-4 p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">1. Patient's HIC Claim No.</div>
              <input
                type="text"
                name="medicalRecordNumber"
                value={formData.medicalRecordNumber}
                onChange={handleChange}
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <div className="text-xs font-semibold text-gray-500 mt-3 mb-1">2. Start of Care Date</div>
              <input
                type="date"
                name="startOfCareDate"
                value={formData.startOfCareDate}
                onChange={handleChange}
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <div className="text-xs font-semibold text-gray-500 mt-3 mb-1">3. Certification Period</div>
              <div className="flex gap-2">
                <input
                  type="date"
                  name="certificationFromDate"
                  value={formData.certificationFromDate}
                  onChange={handleChange}
                  className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  placeholder="From"
                />
                <input
                  type="date"
                  name="certificationToDate"
                  value={formData.certificationToDate}
                  onChange={handleChange}
                  className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  placeholder="To"
                />
              </div>
            </div>

            {/* Patient Name and Address */}
            <div className="col-span-4 p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">4. Patient's Name and Address</div>
              <input
                type="text"
                name="patientName"
                value={formData.patientName}
                onChange={handleChange}
                placeholder="Patient Name"
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <input
                type="text"
                name="patientAddress"
                value={formData.patientAddress}
                onChange={handleChange}
                placeholder="Street Address"
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm mt-1"
              />
              <div className="flex gap-2 mt-1">
                <input
                  type="text"
                  name="patientCity"
                  value={formData.patientCity}
                  onChange={handleChange}
                  placeholder="City"
                  className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                />
                <input
                  type="text"
                  name="patientState"
                  value={formData.patientState}
                  onChange={handleChange}
                  placeholder="State"
                  className="w-16 px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                />
                <input
                  type="text"
                  name="patientZip"
                  value={formData.patientZip}
                  onChange={handleChange}
                  placeholder="ZIP"
                  className="w-20 px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Provider and DOB */}
            <div className="col-span-4 p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">5. Provider No.</div>
              <input
                type="text"
                name="providerNumber"
                value={formData.providerNumber}
                onChange={handleChange}
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <div className="text-xs font-semibold text-gray-500 mt-3 mb-1">6. Patient's Birth Date</div>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <div className="text-xs font-semibold text-gray-500 mt-3 mb-1">7. Sex</div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1">
                  <input type="radio" name="sex" value="Male" checked={formData.sex === 'Male'} onChange={handleChange} className="text-blue-500" />
                  <span className="text-sm">M</span>
                </label>
                <label className="flex items-center gap-1">
                  <input type="radio" name="sex" value="Female" checked={formData.sex === 'Female'} onChange={handleChange} className="text-blue-500" />
                  <span className="text-sm">F</span>
                </label>
              </div>
            </div>
          </div>

          {/* Physician Info */}
          <div className="grid grid-cols-12 border-b divide-x divide-gray-200">
            <div className="col-span-8 p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">8. Physician's Name and Address</div>
              <input
                type="text"
                name="physicianName"
                value={formData.physicianName}
                onChange={handleChange}
                placeholder="Physician Name"
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <div className="grid grid-cols-3 gap-2 mt-1">
                <input
                  type="text"
                  name="physicianAddress"
                  value={formData.physicianAddress}
                  onChange={handleChange}
                  placeholder="Address"
                  className="col-span-2 px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                />
                <input
                  type="tel"
                  name="physicianPhone"
                  value={formData.physicianPhone}
                  onChange={handleChange}
                  placeholder="Phone"
                  className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
            <div className="col-span-4 p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">9. Date of Verbal SOC Where Applicable</div>
              <input
                type="date"
                name="verbalOrderDate"
                value={formData.verbalOrderDate}
                onChange={handleChange}
                className="w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Diagnoses */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-2">10. Primary Diagnosis and 11-12. Other Diagnoses</div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <input
                type="text"
                name="primaryDiagnosis"
                value={formData.primaryDiagnosis}
                onChange={handleChange}
                placeholder="Primary Diagnosis"
                className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <input
                type="text"
                name="primaryDiagnosisCode"
                value={formData.primaryDiagnosisCode}
                onChange={handleChange}
                placeholder="ICD-10 Code"
                className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
              <input
                type="date"
                name="primaryDiagnosisDate"
                value={formData.primaryDiagnosisDate}
                onChange={handleChange}
                className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    name={`otherDiagnosis${i}`}
                    value={formData[`otherDiagnosis${i}`]}
                    onChange={handleChange}
                    placeholder={`Other Diagnosis ${i}`}
                    className="flex-1 px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    name={`otherDiagnosis${i}Code`}
                    value={formData[`otherDiagnosis${i}Code`]}
                    onChange={handleChange}
                    placeholder="ICD Code"
                    className="w-24 px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* DME, Safety, Allergies, Nutrition */}
          <div className="grid grid-cols-2 divide-x divide-gray-200 border-b">
            <div className="p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">13. DME and Supplies</div>
              <textarea
                name="dmeAndSupplies"
                value={formData.dmeAndSupplies}
                onChange={handleChange}
                rows={2}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-500 text-sm"
              />
            </div>
            <div className="p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">14. Safety Measures</div>
              <textarea
                name="safetyMeasures"
                value={formData.safetyMeasures}
                onChange={handleChange}
                rows={2}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 divide-x divide-gray-200 border-b">
            <div className="p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">15. Allergies</div>
              <textarea
                name="allergies"
                value={formData.allergies}
                onChange={handleChange}
                rows={2}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-500 text-sm"
              />
            </div>
            <div className="p-4">
              <div className="text-xs font-semibold text-gray-500 mb-1">16. Nutritional Requirements</div>
              <textarea
                name="nutritionalRequirements"
                value={formData.nutritionalRequirements}
                onChange={handleChange}
                rows={2}
                className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Functional Limitations */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-2">17. Functional Limitations</div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
              {[
                { key: 'amputation', label: '1. Amputation' },
                { key: 'bowelBladder', label: '2. Bowel/Bladder' },
                { key: 'contracture', label: '3. Contracture' },
                { key: 'hearing', label: '4. Hearing' },
                { key: 'paralysis', label: '5. Paralysis' },
                { key: 'endurance', label: '6. Endurance' },
                { key: 'ambulation', label: '7. Ambulation' },
                { key: 'speech', label: '8. Speech' },
                { key: 'legallyBlind', label: '9. Legally Blind' },
                { key: 'dyspnea', label: '10. Dyspnea' },
                { key: 'other', label: '11. Other' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name={`functionalLimitations_${key}`}
                    checked={formData.functionalLimitations[key]}
                    onChange={handleChange}
                    className="w-3 h-3 text-blue-500"
                  />
                  {label}
                </label>
              ))}
            </div>
            {formData.functionalLimitations.other && (
              <input
                type="text"
                name="functionalLimitations_otherDescription"
                value={formData.functionalLimitations.otherDescription}
                onChange={handleChange}
                placeholder="Specify other"
                className="mt-2 w-full px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
              />
            )}
          </div>

          {/* Activities Permitted */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-2">18. Activities Permitted</div>
            <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
              {[
                { key: 'bedrest', label: 'A. Complete Bedrest' },
                { key: 'bedrestBRP', label: 'B. Bedrest BRP' },
                { key: 'upAsToleraded', label: 'C. Up as Tolerated' },
                { key: 'transferBedChair', label: 'D. Transfer Bed/Chair' },
                { key: 'exercises', label: 'E. Exercises' },
                { key: 'partialWeightBearing', label: 'F. Partial Weight Bearing' },
                { key: 'independentAtHome', label: 'G. Independent at Home' },
                { key: 'crutches', label: 'H. Crutches' },
                { key: 'cane', label: 'I. Cane' },
                { key: 'wheelchair', label: 'J. Wheelchair' },
                { key: 'walker', label: 'K. Walker' },
                { key: 'noRestrictions', label: 'L. No Restrictions' },
                { key: 'other', label: 'M. Other' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name={`activitiesPermitted_${key}`}
                    checked={formData.activitiesPermitted[key]}
                    onChange={handleChange}
                    className="w-3 h-3 text-blue-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Mental Status */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-2">19. Mental Status</div>
            <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
              {[
                { key: 'oriented', label: '1. Oriented' },
                { key: 'comatose', label: '2. Comatose' },
                { key: 'forgetful', label: '3. Forgetful' },
                { key: 'depressed', label: '4. Depressed' },
                { key: 'disoriented', label: '5. Disoriented' },
                { key: 'lethargic', label: '6. Lethargic' },
                { key: 'agitated', label: '7. Agitated' },
                { key: 'other', label: '8. Other' },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name={`mentalStatus_${key}`}
                    checked={formData.mentalStatus[key]}
                    onChange={handleChange}
                    className="w-3 h-3 text-blue-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Prognosis */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-1">20. Prognosis</div>
            <div className="flex gap-4">
              {['Poor', 'Guarded', 'Fair', 'Good', 'Excellent'].map(prog => (
                <label key={prog} className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    name="prognosis"
                    value={prog}
                    checked={formData.prognosis === prog}
                    onChange={handleChange}
                    className="text-blue-500"
                  />
                  {prog}
                </label>
              ))}
            </div>
          </div>

          {/* Orders for Discipline and Treatments */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-1">21. Orders for Discipline and Treatments (Specify Amount/Frequency/Duration)</div>
            <textarea
              name="therapyOrders"
              value={formData.therapyOrders}
              onChange={handleChange}
              rows={4}
              placeholder="SN: Skilled nursing visits for wound care, medication management...
PT: Physical therapy for gait training, balance exercises...
OT: Occupational therapy for ADL training...
ST: Speech therapy for swallowing evaluation...
MSW: Medical social worker for resource coordination...
HHA: Home health aide for personal care..."
              className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-500 text-sm"
            />
          </div>

          {/* Goals */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-1">22. Goals/Rehabilitation Potential/Discharge Plans</div>
            <textarea
              name="goalsRehabPotential"
              value={formData.goalsRehabPotential}
              onChange={handleChange}
              rows={3}
              className="w-full px-2 py-1 border border-gray-300 rounded focus:border-blue-500 text-sm"
            />
          </div>

          {/* Medications */}
          <div className="border-b p-4">
            <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-2">
              <Pill className="w-4 h-4 text-blue-500" />
              24. Medications: Dose/Frequency/Route (New/Changed)
            </div>
            <div className="space-y-2">
              {formData.medications.map((med, index) => (
                <div key={index} className="grid grid-cols-4 gap-2">
                  <input
                    type="text"
                    value={med.name}
                    onChange={(e) => handleMedicationChange(index, 'name', e.target.value)}
                    placeholder="Medication name"
                    className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    value={med.dosage}
                    onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                    placeholder="Dosage"
                    className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    value={med.frequency}
                    onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                    placeholder="Frequency"
                    className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  />
                  <input
                    type="text"
                    value={med.route}
                    onChange={(e) => handleMedicationChange(index, 'route', e.target.value)}
                    placeholder="Route"
                    className="px-2 py-1 border-b border-gray-300 focus:border-blue-500 text-sm"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Physician Certification */}
          <div className="p-4">
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="attendingPhysicianCertification"
                  checked={formData.attendingPhysicianCertification}
                  onChange={handleChange}
                  className="w-5 h-5 rounded text-blue-500 focus:ring-blue-500 mt-0.5"
                />
                <span className="text-sm text-gray-700">
                  <strong>ATTENDING PHYSICIAN'S SIGNATURE AND DATE SIGNED:</strong> I certify/recertify that this patient is confined to his/her home and needs intermittent skilled nursing care, physical therapy and/or speech therapy or continues to need occupational therapy. The patient is under my care, and I have authorized the services on this plan of care and will periodically review the plan.
                </span>
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Physician Signature</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                  <SignatureCanvas
                    ref={physicianSignatureRef}
                    canvasProps={{
                      className: 'w-full h-24 bg-white',
                      style: { width: '100%', height: '96px' }
                    }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => physicianSignatureRef.current?.clear()}
                  className="mt-1 text-xs text-red-600 hover:text-red-700"
                >
                  Clear
                </button>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date Signed</label>
                <input
                  type="date"
                  name="physicianSignatureDate"
                  value={formData.physicianSignatureDate}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CMS485TemplateForm;
